import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loader, Button } from "@/components/PageUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, Copy } from "lucide-react";

const empty = { title: "", date: "", duration: "", warmup: "", drills: "", scrimmage: "", conditioning: "", notes: "" };

export default function Training() {
  const [plans, setPlans] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/training-plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    try { await api.post("/training-plans", form); toast.success("Plan saved"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/training-plans/${id}`); load(); };
  const reuse = (p) => { setForm({ ...p, title: `${p.title} (copy)`, date: "" }); setOpen(true); };

  if (!plans) return <Loader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Training Planner" subtitle="Build practice plans and reuse them every week." testid="training-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="brand-gradient text-white border-0 hover:opacity-90" data-testid="add-plan-btn"><Plus size={16} className="mr-1.5" /> New plan</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="plan-dialog">
              <DialogHeader><DialogTitle>Practice plan</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="plan-title-input" /></div>
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Duration</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="90 min" className="mt-1.5" /></div>
                </div>
                {[["warmup", "Warm-up"], ["drills", "Drills"], ["scrimmage", "Scrimmage"], ["conditioning", "Conditioning"], ["notes", "Notes"]].map(([k, l]) => (
                  <div key={k}><Label>{l}</Label><Textarea value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`plan-${k}-input`} /></div>
                ))}
              </div>
              <DialogFooter><Button onClick={create} className="brand-gradient text-white border-0" data-testid="save-plan-btn">Save plan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      {plans.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No training plans" desc="Create reusable practice plans with warm-ups, drills and conditioning." testid="empty-training" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-card p-6" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-heading font-bold text-lg">{p.title}</h3><p className="text-sm text-muted-foreground">{p.date || "No date"} {p.duration && `· ${p.duration}`}</p></div>
                <div className="flex gap-1">
                  <button onClick={() => reuse(p)} className="text-muted-foreground hover:text-primary" data-testid={`reuse-plan-${p.id}`}><Copy size={15} /></button>
                  <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-plan-${p.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                {p.warmup && <p><span className="text-secondary font-semibold">Warm-up:</span> <span className="text-muted-foreground">{p.warmup}</span></p>}
                {p.drills && <p><span className="text-secondary font-semibold">Drills:</span> <span className="text-muted-foreground">{p.drills}</span></p>}
                {p.conditioning && <p><span className="text-secondary font-semibold">Conditioning:</span> <span className="text-muted-foreground">{p.conditioning}</span></p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
